import * as THREE from "three";
import { autofocusCameraToSelection } from "../selected-mesh-visibility";
import { restoreCameraHome } from "./cameraHome";
import { CAMERA_VIEW_SMOOTH_DURATION_MS, readCameraViewPreferences } from "./cameraViewPreferences";
import { getViewerHandles } from "./viewerHandles";
import { logger } from "../utils/logger";

/** Orthogonal viewpoints used by the camera view menu and keyboard shortcuts. */
export const CAMERA_VIEWPOINTS = [
  {
    key: "top",
    label: "Top view",
    text: "T",
    direction: [0, 1, 0],
    up: [0, 0, -1],
    gridArea: "top",
  },
  {
    key: "bottom",
    label: "Bottom view",
    text: "B",
    direction: [0, -1, 0],
    up: [0, 0, 1],
    gridArea: "bottom",
  },
  {
    key: "front",
    label: "Front view",
    text: "F",
    direction: [0, 0, 1],
    up: [0, 1, 0],
    gridArea: "front",
  },
  {
    key: "back",
    label: "Back view",
    text: "Bk",
    direction: [0, 0, -1],
    up: [0, 1, 0],
    gridArea: "back",
  },
  {
    key: "left",
    label: "Left view",
    text: "L",
    direction: [-1, 0, 0],
    up: [0, 1, 0],
    gridArea: "left",
  },
  {
    key: "right",
    label: "Right view",
    text: "R",
    direction: [1, 0, 0],
    up: [0, 1, 0],
    gridArea: "right",
  },
];

const DEFAULT_VIEW_DIRECTION = new THREE.Vector3(0.6, 0.5, 1).normalize();
const VIEW_DISTANCE_PADDING = 1.8;
const ORBIT_UP = new THREE.Vector3(0, 1, 0);
const ORBIT_POLE_EPSILON = 0.01;

function getUsableBounds(object) {
  if (!object) return null;
  const box = new THREE.Box3().setFromObject(object);
  return box.isEmpty() ? null : box;
}

function getModelBounds(scene) {
  return getUsableBounds(scene?.getObjectByName?.("modelGroup"));
}

function getCameraDistanceForBounds(camera, box) {
  if (!camera || !box) return null;
  const size = new THREE.Vector3();
  box.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z);
  if (!(maxDim > 0)) return null;
  const fov = THREE.MathUtils.degToRad(camera.fov || 75);
  return (maxDim / 2 / Math.tan(fov / 2)) * VIEW_DISTANCE_PADDING;
}

function animateCameraTo({
  camera,
  controls,
  target,
  position,
  orbitUp,
  durationMs = CAMERA_VIEW_SMOOTH_DURATION_MS,
}) {
  if (!camera || !controls || !target || !position || !orbitUp) return;

  if (durationMs <= 0) {
    camera.position.copy(position);
    camera.up.copy(orbitUp).normalize();
    controls.target.copy(target);
    camera.lookAt(controls.target);
    camera.updateProjectionMatrix?.();
    controls.update();
    return;
  }

  const startPosition = camera.position.clone();
  const startTarget = controls.target.clone();
  const startUp = camera.up.clone();
  const startMs = performance.now();

  const step = (nowMs) => {
    const rawT = Math.min(1, (nowMs - startMs) / durationMs);
    const t = 1 - Math.pow(1 - rawT, 3);
    camera.position.lerpVectors(startPosition, position, t);
    camera.up.lerpVectors(startUp, orbitUp, t).normalize();
    controls.target.lerpVectors(startTarget, target, t);
    camera.lookAt(controls.target);
    camera.updateProjectionMatrix?.();
    controls.update();
    if (rawT < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}

function projectOntoOrbitPlane(vector) {
  return vector.clone().addScaledVector(ORBIT_UP, -vector.dot(ORBIT_UP));
}

function getGroundScreenRight(camera) {
  camera.updateMatrixWorld?.(true);
  const screenRight = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
  const groundScreenRight = projectOntoOrbitPlane(screenRight);
  return groundScreenRight.lengthSq() > 1e-8 ? groundScreenRight.normalize() : null;
}

export function resolveOrbitCameraDirection(direction, viewUp, currentScreenRight = null) {
  const directionVector = new THREE.Vector3(...direction).normalize();
  if (Math.abs(directionVector.dot(ORBIT_UP)) < 1 - ORBIT_POLE_EPSILON) {
    return directionVector;
  }

  const currentScreenRightVector = currentScreenRight
    ? projectOntoOrbitPlane(new THREE.Vector3(...currentScreenRight))
    : null;
  if (currentScreenRightVector && currentScreenRightVector.lengthSq() > 1e-8) {
    const horizontalOffset = currentScreenRightVector.normalize().cross(ORBIT_UP).normalize();
    return directionVector.addScaledVector(horizontalOffset, ORBIT_POLE_EPSILON).normalize();
  }

  const viewUpVector = new THREE.Vector3(...viewUp).normalize();
  const poleSign = Math.sign(directionVector.dot(ORBIT_UP)) || 1;
  return directionVector.addScaledVector(viewUpVector, -poleSign * ORBIT_POLE_EPSILON).normalize();
}

/**
 * @param {readonly [number, number, number]} direction
 * @param {readonly [number, number, number]} up
 * @param {{ fitAll?: boolean, motionMode?: import("./cameraViewPreferences").CameraMotionMode }} [options]
 */
export function moveCameraToDirection(direction, up, options = {}) {
  const { fitAll = false, motionMode: motionModeOption } = options;
  const motionMode = motionModeOption ?? readCameraViewPreferences().motionMode;

  const { camera, controls, scene } = getViewerHandles();
  if (!camera || !controls) {
    logger.warn("[cameraViewActions] Cannot change view before camera controls are ready.");
    return;
  }

  const bounds = fitAll ? getModelBounds(scene) : null;
  const target = new THREE.Vector3();
  if (bounds) {
    bounds.getCenter(target);
  } else {
    target.copy(controls.target);
  }

  const currentDistance = Math.max(camera.position.distanceTo(controls.target), 0.1);
  const fitDistance = bounds ? getCameraDistanceForBounds(camera, bounds) : null;
  const distance = Math.max(currentDistance, fitDistance || 0.1);
  const currentScreenRight = getGroundScreenRight(camera)?.toArray() ?? null;
  const directionVector = resolveOrbitCameraDirection(direction, up, currentScreenRight);
  const position = target.clone().addScaledVector(directionVector, distance);

  animateCameraTo({
    camera,
    controls,
    target,
    position,
    orbitUp: ORBIT_UP,
    durationMs: motionMode === "jump" ? 0 : CAMERA_VIEW_SMOOTH_DURATION_MS,
  });
}

/**
 * @param {{
 *   selectedBody: unknown,
 *   selectedMesh: unknown,
 *   selectedGeom: unknown,
 *   selectedJoint: unknown,
 *   selectedSite: unknown,
 *   selectedInertial: unknown,
 * }} selection
 * @param {{ autofocusMode?: import("./cameraViewPreferences").CameraAutofocusMode, motionMode?: import("./cameraViewPreferences").CameraMotionMode }} [preferences]
 */
export function focusCameraOnSelection(selection, preferences = readCameraViewPreferences()) {
  const didFocus = autofocusCameraToSelection({
    bodyUuid: selection.selectedBody,
    meshUuid: selection.selectedMesh,
    collisionGeomUuid: selection.selectedGeom,
    jointUuid: selection.selectedJoint,
    siteUuid: selection.selectedSite,
    inertialUuid: selection.selectedInertial,
    framing: preferences.autofocusMode === "center" ? "recenter" : "fit",
    durationMs: preferences.motionMode === "jump" ? 0 : CAMERA_VIEW_SMOOTH_DURATION_MS,
  });
  if (!didFocus) {
    logger.warn(
      "[cameraViewActions] Select a body, mesh, joint, site, geom, or inertial before using focus."
    );
  }
}

/** Restore saved home (if any), otherwise default diagonal view framed on scene bounds. */
export function resetCameraToHomeOrDefault(preferences = readCameraViewPreferences()) {
  if (restoreCameraHome(preferences.motionMode === "jump" ? 0 : CAMERA_VIEW_SMOOTH_DURATION_MS))
    return;

  const { camera, controls, scene } = getViewerHandles();
  if (!camera || !controls) {
    logger.warn("[cameraViewActions] Cannot reset camera before camera controls are ready.");
    return;
  }

  const bounds = getModelBounds(scene);
  const target = new THREE.Vector3();
  if (bounds) bounds.getCenter(target);
  const distance = bounds ? getCameraDistanceForBounds(camera, bounds) : 2;
  const position = target.clone().addScaledVector(DEFAULT_VIEW_DIRECTION, distance || 2);
  animateCameraTo({
    camera,
    controls,
    target,
    position,
    orbitUp: ORBIT_UP,
    durationMs: preferences.motionMode === "jump" ? 0 : CAMERA_VIEW_SMOOTH_DURATION_MS,
  });
}

/** Frame the full scene from the default diagonal direction (zoom-to-fit). */
export function fitCameraToEntireScene(preferences = readCameraViewPreferences()) {
  moveCameraToDirection(
    [DEFAULT_VIEW_DIRECTION.x, DEFAULT_VIEW_DIRECTION.y, DEFAULT_VIEW_DIRECTION.z],
    [0, 1, 0],
    { fitAll: true, motionMode: preferences.motionMode }
  );
}
